import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { TransportDeskState } from './transport-desk-model';

// The Transport desk's one-line goal: a segmented bar (arrived → en route →
// return scheduled → own means → still to move) + the number the operator works
// to zero. All-green when the shift is done. Token-native; same semantic hues as
// the Hotels desk goal bar.
//
// AGGREGATION NOTE — the sky "En route" segment counts EN_ROUTE + WAITING: a
// passenger whose vehicle is boarding, assigned, or already requested from the
// vendor "has a vehicle on the way" (the settled sentence's own words), and
// neither lane is operator-actionable. The amber remainder is NEEDS_TRANSPORT
// alone — SCHEDULED (return legs not due yet) is NEVER amber.

const SEGMENTS: {
  key: 'ARRIVED' | 'EN_ROUTE' | 'SCHEDULED' | 'OWN_MEANS' | 'REMAINDER';
  labelKey: string;
  barClass: string;
  dotClass: string;
}[] = [
  {
    key: 'ARRIVED',
    labelKey: 'goal.segment.arrived',
    barClass: 'bg-emerald-500',
    dotClass: 'bg-emerald-500',
  },
  {
    key: 'EN_ROUTE',
    labelKey: 'goal.segment.enRoute',
    barClass: 'bg-sky-500',
    dotClass: 'bg-sky-500',
  },
  {
    key: 'SCHEDULED',
    labelKey: 'goal.segment.scheduled',
    barClass: 'bg-sky-300 dark:bg-sky-800',
    dotClass: 'bg-sky-300 dark:bg-sky-800',
  },
  {
    key: 'OWN_MEANS',
    labelKey: 'goal.segment.ownMeans',
    barClass: 'bg-slate-400 dark:bg-slate-500',
    dotClass: 'bg-slate-400 dark:bg-slate-500',
  },
  {
    key: 'REMAINDER',
    labelKey: 'goal.segment.remainder',
    barClass: 'bg-amber-500',
    dotClass: 'bg-amber-500',
  },
];

export function TransportGoalBar({
  desk,
  truncated,
}: {
  desk: TransportDeskState;
  truncated: boolean;
}) {
  const { t } = useTranslation('transport');
  // An EMPTY walk is not a victory — a cold roster / upstream blip must read as
  // "nothing loaded", never as "everyone moved".
  if (desk.rows.length === 0) {
    return (
      <section
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="transport-goal-bar"
      >
        <p className="text-sm text-muted-foreground">{t('goal.emptyWalk')}</p>
      </section>
    );
  }
  // A roster with no journeys (no confirmed hotels) is calm, not green.
  if (desk.goalPnrs === 0) {
    return (
      <section
        className="rounded-lg border border-border bg-card px-4 py-3"
        data-testid="transport-goal-bar"
      >
        <p className="text-sm text-muted-foreground" data-testid="transport-goal-none">
          {t('noJourneysYet')}
        </p>
      </section>
    );
  }

  const remainder = desk.counts.NEEDS_TRANSPORT.pnrs;
  const enRoute = desk.counts.EN_ROUTE.pnrs + desk.counts.WAITING.pnrs;
  const values: Record<(typeof SEGMENTS)[number]['key'], number> = {
    ARRIVED: desk.counts.ARRIVED.pnrs,
    EN_ROUTE: enRoute,
    SCHEDULED: desk.counts.SCHEDULED.pnrs,
    OWN_MEANS: desk.counts.OWN_MEANS.pnrs,
    REMAINDER: remainder,
  };
  const total = Math.max(1, desk.goalPnrs);

  return (
    <section
      className={cn(
        'rounded-lg border px-4 py-3',
        desk.allSettled
          ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30'
          : 'border-border bg-card',
      )}
      data-testid="transport-goal-bar"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {desk.allSettled ? (
          <p
            className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
            data-testid="transport-goal-settled"
          >
            {t('goal.settled')}
          </p>
        ) : (
          <p className="text-sm">
            <span
              className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400"
              data-testid="transport-goal-remainder"
            >
              {remainder}
            </span>{' '}
            <span className="font-semibold">{t('goal.pnrsToMove', { count: remainder })}</span>{' '}
            <span className="text-muted-foreground">
              {t('goal.detail', { pax: desk.counts.NEEDS_TRANSPORT.pax, enRoute })}
            </span>
          </p>
        )}
        <p className="text-xs text-muted-foreground" data-testid="transport-goal-denominator">
          {t(truncated ? 'goal.denominatorPartial' : 'goal.denominator', {
            pnrs: desk.goalPnrs,
            pax: desk.goalPax,
          })}
        </p>
      </div>
      <div className="mt-3 flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted">
        {SEGMENTS.map((seg) =>
          values[seg.key] > 0 ? (
            <div
              key={seg.key}
              className={cn('h-full', seg.barClass)}
              style={{ width: `${(values[seg.key] / total) * 100}%` }}
              title={t('goal.segmentTitle', {
                label: t(seg.labelKey),
                value: values[seg.key],
              })}
            />
          ) : null,
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {SEGMENTS.map((seg) => (
          <span
            key={seg.key}
            className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
            title={seg.key === 'EN_ROUTE' ? t('goal.enRouteHint') : undefined}
          >
            <span aria-hidden className={cn('h-2 w-2 rounded-full', seg.dotClass)} />
            {values[seg.key]} {t(seg.labelKey)}
          </span>
        ))}
      </div>
    </section>
  );
}
